import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Brain, Plus, Trash2, ExternalLink, ChevronRight, ChevronLeft,
  AlertTriangle, Pencil, Clock, CheckCircle2, XCircle, RefreshCw,
  X, Activity, Hash, UserPlus, Megaphone,
  Gauge, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { BrainHealthTab } from './BrainHealthTab';
import { BrainQualityTab } from './BrainQualityTab';
import { BrainPipelineGovernanceTab } from './BrainPipelineGovernanceTab';
import { BrainProfileForm } from './BrainProfileForm';
import { StepBar } from '@/components/brain/StepBar';
import { SlackChannelPicker } from '@/components/brain/SlackChannelPicker';
import { useAuth } from '@/contexts/SharedAuthContext';
import { BRAIN_CLIENTS } from '../../../../shared/types/brain';
import { slugify as slugifyShared } from '../../../../shared/text-normalize';
import { PrOpenedNotice } from '@/components/brain/PrOpenedNotice';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SlackChannel {
  channelId:   string;
  channelName: string;
}

interface FormState {
  displayName:          string;
  clientId:             string;
  clientIdTouched:      boolean;
  slackChannels:        SlackChannel[];
  hasExternalWorkspace: boolean;
  externalChannelNames: string[];
  /** Nombres en Capacity_Final (revenue), separados por coma. */
  bqName:               string;
  /** Nombres en el asignador de horas. */
  bqAllocator:          string[];
  /** Nombre en Jotform (NPS) — uno solo. */
  bqJotform:            string;
  /** Ya precargamos las coincidencias de BigQuery (no volver a pisar lo que editó la persona). */
  bqAuto:               boolean;
  /** Blindaje: si no hay nombre de BigQuery, hay que confirmarlo a propósito. */
  noBq:                 boolean;
}

type FormMode =
  | { type: 'new' }
  | { type: 'edit'; branchName: string; prNumber: number; prUrl: string; originalClientId: string };

const STEP_COUNT = 5;

// El slug del id sale de shared/text-normalize.ts (sin tildes): "Gestión" → "gestion" (19/10/2026).
const slugify = (name: string): string => slugifyShared(name, 30);

function emptyForm(): FormState {
  return {
    displayName: '', clientId: '', clientIdTouched: false,
    slackChannels: [],
    hasExternalWorkspace: false,
    externalChannelNames: [],
    bqName: '',
    bqAllocator: [],
    bqJotform: '',
    bqAuto: false,
    noBq: false,
  };
}

// ─── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: 'open' | 'merged' | 'closed' }) {
  const { t } = useTranslation();
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        <Clock className="w-3 h-3" /> {t('brain.clients.my_proposals.status_open')}
      </span>
    );
  }
  if (status === 'merged') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> {t('brain.clients.my_proposals.status_merged')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
      <XCircle className="w-3 h-3" /> {t('brain.clients.my_proposals.status_closed')}
    </span>
  );
}

// Barra de pasos: components/brain/StepBar.tsx (la comparte el alta de perfil PML).
const CLIENT_STEP_KEYS = Array.from({ length: STEP_COUNT }, (_, i) => `brain.clients.steps.${i}`);

// ─── Step 1 ────────────────────────────────────────────────────────────────────

function Step1({ form, setForm, isEdit }: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  isEdit: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div>
        <Label htmlFor="displayName">{t('brain.clients.step1.display_name_label')}</Label>
        <Input
          id="displayName" className="mt-1" placeholder={t('brain.clients.step1.display_name_placeholder')}
          value={form.displayName}
          onChange={(e) => {
            const val = e.target.value;
            setForm((f) => ({
              ...f, displayName: val,
              clientId: f.clientIdTouched ? f.clientId : slugify(val),
            }));
          }}
        />
      </div>
      <div>
        <Label htmlFor="clientId">{t('brain.clients.step1.client_id_label')}</Label>
        <Input
          id="clientId" className="mt-1 font-mono text-sm" placeholder={t('brain.clients.step1.client_id_placeholder')}
          value={form.clientId} disabled={isEdit}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              clientId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
              clientIdTouched: true,
            }))
          }
        />
        <p className="text-xs text-gray-500 mt-1">
          {isEdit ? t('brain.clients.step1.client_id_hint_edit') : t('brain.clients.step1.client_id_hint')}
        </p>
      </div>
    </div>
  );
}

// ─── Step 2 ────────────────────────────────────────────────────────────────────

function Step2({ form, setForm }: { form: FormState; setForm: React.Dispatch<React.SetStateAction<FormState>> }) {
  const { t } = useTranslation();
  const [externalInput, setExternalInput] = useState('');

  const addExternalChannel = () => {
    const name = externalInput.trim().replace(/^#/, '');
    if (!name) return;
    setForm((f) => ({
      ...f,
      externalChannelNames: f.externalChannelNames.includes(name)
        ? f.externalChannelNames
        : [...f.externalChannelNames, name],
    }));
    setExternalInput('');
  };

  const removeExternalChannel = (name: string) => {
    setForm((f) => ({ ...f, externalChannelNames: f.externalChannelNames.filter((n) => n !== name) }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700">
          <strong>{t('brain.clients.step2.important')}</strong>{' '}
          {t('brain.clients.step2.disclaimer')}
        </p>
      </div>

      {/* Section: Winclap workspace channels (picker compartido con el alta de perfil PML) */}
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-2">{t('brain.clients.canales_en_el_slack_de_winclap')}</p>
        <SlackChannelPicker
          selected={form.slackChannels.map((ch) => ch.channelId)}
          knownNames={Object.fromEntries(form.slackChannels.map((ch) => [ch.channelId, ch.channelName]))}
          onChange={(ids, names) => setForm((f) => ({ ...f, slackChannels: ids.map((id) => ({ channelId: id, channelName: f.slackChannels.find((c) => c.channelId === id)?.channelName ?? names[id] ?? id })) }))}
        />
      </div>

      {/* Section: External workspace */}
      <div className="border-t pt-4 space-y-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-gray-300 text-[#FF6B6B] focus:ring-[#FF6B6B]"
            checked={form.hasExternalWorkspace}
            onChange={(e) => setForm((f) => ({ ...f, hasExternalWorkspace: e.target.checked }))}
          />
          <div>
            <p className="text-sm font-medium text-gray-800">{t('brain.clients.el_cliente_tiene_su_propio_workspace')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('brain.clients.marcalo_si_usan_un_slack_externo')}</p>
          </div>
        </label>

        {form.hasExternalWorkspace && (
          <div className="ml-6 space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>{t('brain.clients.accion_requerida')}</strong>{' '}
                <Trans i18nKey="brain.clients.pedile_al_cliente_que_invite" values={{ email: 'solange.guerrero@winclap.com' }} components={{ code: <span className="font-mono font-semibold" /> }} />
              </p>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-600 mb-1.5">
                {t('brain.clients.canales_del_workspace_externo')} <span className="text-gray-400 font-normal">{t('brain.clients.opcional_por_ahora')}</span>
              </p>

              {form.externalChannelNames.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {form.externalChannelNames.map((name) => (
                    <span key={name}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 font-medium">
                      #{name}
                      <button onClick={() => removeExternalChannel(name)}
                        className="ml-0.5 hover:opacity-60 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  value={externalInput}
                  onChange={(e) => setExternalInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExternalChannel(); } }}
                  placeholder={t('brain.clients.nombre_del_canal_sin')}
                  className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:outline-hidden focus:ring-1 focus:ring-blue-400"
                />
                <button
                  onClick={addExternalChannel}
                  disabled={!externalInput.trim()}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  Agregar
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('brain.clients.podes_agregar_los_canales_ahora_o')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3 ────────────────────────────────────────────────────────────────────

function Step3({ form, setForm }: { form: FormState; setForm: React.Dispatch<React.SetStateAction<FormState>> }) {
  const { t } = useTranslation();
  const q = form.displayName.trim();
  // Buscar en BigQuery es responsabilidad nuestra, no del proponente: traemos
  // TODAS las coincidencias de las 3 fuentes y las precargamos; los chips sirven
  // para sacar las que no sean de este cliente.
  const suggest = trpc.brain.suggestBqNames.useQuery({ q }, { enabled: q.length >= 2, staleTime: 60_000 });
  const capacity = form.bqName.split(',').map((x) => x.trim()).filter(Boolean);
  useEffect(() => {
    if (!suggest.data || form.bqAuto) return;
    const d = suggest.data;
    setForm((f) => ({
      ...f,
      bqAuto: true,
      bqName:      f.bqName.trim() ? f.bqName : d.capacity.map((r) => r.name).join(', '),
      bqAllocator: f.bqAllocator.length ? f.bqAllocator : d.allocator.map((r) => r.name),
      bqJotform:   f.bqJotform || (d.jotform[0]?.name ?? ''),
    }));
  }, [suggest.data, form.bqAuto, setForm]);

  const toggleCapacity = (name: string) => {
    const next = capacity.includes(name) ? capacity.filter((x) => x !== name) : [...capacity, name];
    setForm((f) => ({ ...f, bqName: next.join(', '), noBq: false }));
  };
  const toggleAllocator = (name: string) =>
    setForm((f) => ({ ...f, noBq: false, bqAllocator: f.bqAllocator.includes(name) ? f.bqAllocator.filter((x) => x !== name) : [...f.bqAllocator, name] }));
  const pickJotform = (name: string) => setForm((f) => ({ ...f, noBq: false, bqJotform: f.bqJotform === name ? '' : name }));

  const total = suggest.data ? suggest.data.capacity.length + suggest.data.allocator.length + suggest.data.jotform.length : 0;
  const nothingChosen = capacity.length === 0 && form.bqAllocator.length === 0 && !form.bqJotform;
  const chip = (name: string, rows: number, on: boolean, onClick: () => void) => (
    <button
      key={name} type="button" onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${on ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'bg-white text-gray-500 line-through hover:border-gray-400'}`}
      title={t('brain.clients.step3.rows', { count: rows })}
    >
      {name} <span className="text-gray-400 no-underline">· {rows}</span>
    </button>
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">{t('brain.clients.step3.auto_hint')}</p>

      <div className="rounded-lg border bg-gray-50 p-3 space-y-2.5">
        {suggest.isLoading && <p className="text-xs text-gray-400">{t('brain.clients.step3.suggestions_loading')}</p>}
        {suggest.data && total > 0 && (
          <p className="text-xs font-medium text-emerald-800">{t('brain.clients.step3.auto_loaded', { count: total, name: q })}</p>
        )}
        {suggest.data && total === 0 && <p className="text-xs text-gray-500">{t('brain.clients.step3.suggestions_none', { name: q })}</p>}
        {suggest.data && suggest.data.capacity.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-gray-500 w-24 shrink-0">{t('brain.clients.step3.src_capacity')}</span>
            {suggest.data.capacity.map((r) => chip(r.name, r.rows, capacity.includes(r.name), () => toggleCapacity(r.name)))}
          </div>
        )}
        {suggest.data && suggest.data.allocator.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-gray-500 w-24 shrink-0">{t('brain.clients.step3.src_allocator')}</span>
            {suggest.data.allocator.map((r) => chip(r.name, r.rows, form.bqAllocator.includes(r.name), () => toggleAllocator(r.name)))}
          </div>
        )}
        {suggest.data && suggest.data.jotform.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-gray-500 w-24 shrink-0">{t('brain.clients.step3.src_jotform')}</span>
            {suggest.data.jotform.map((r) => chip(r.name, r.rows, form.bqJotform === r.name, () => pickJotform(r.name)))}
            <span className="text-[11px] text-gray-400">{t('brain.clients.step3.jotform_one')}</span>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="bqName">{t('brain.clients.step3.bq_label')}</Label>
        <Input id="bqName" className="mt-1 text-sm" placeholder={t('brain.clients.step3.bq_placeholder')}
          value={form.bqName} onChange={(e) => setForm((f) => ({ ...f, bqName: e.target.value, bqAuto: true, noBq: e.target.value.trim() ? false : f.noBq }))} />
        <p className="text-xs text-gray-500 mt-1">{t('brain.clients.step3.bq_hint')} {t('brain.clients.step3.multi_hint')}</p>
      </div>

      {nothingChosen && !suggest.isLoading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-xs text-amber-900">{t('brain.clients.step3.no_bq_warn')}</p>
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input type="checkbox" className="mt-0.5" checked={form.noBq} onChange={(e) => setForm((f) => ({ ...f, noBq: e.target.checked }))} />
            <span>{t('brain.clients.step3.no_bq_confirm')}</span>
          </label>
        </div>
      )}
    </div>
  );
}

// ─── Step 4 ────────────────────────────────────────────────────────────────────

function Step4({ form }: { form: FormState }) {
  const { t } = useTranslation();
  const clientUpper = form.clientId.toUpperCase();
  const TEAM_CODES = ['OML', 'PML', 'CONTENT', 'DATA', 'GP', 'BUSINESS', 'GENERAL'] as const;
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-700">
        {t('brain.clients.step4.intro_start')}{' '}
        <strong>{form.displayName || form.clientId}</strong>{' '}
        {t('brain.clients.step4.intro_end')}
      </p>
      <div className="p-3 rounded-lg bg-gray-50 border font-mono text-sm space-y-1">
        {(['OML', 'PML', 'CONTENT', 'DATA'] as const).map((team) => (
          <div key={team} className="text-gray-700">
            <span className="text-[#FF6B6B]">[{clientUpper}-{team}]</span> {t('brain.clients.step4.meeting_name')}
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border text-sm">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600 text-xs">{t('brain.clients.step4.code_header')}</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600 text-xs">{t('brain.clients.step4.team_header')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {TEAM_CODES.map((code) => (
              <tr key={code}>
                <td className="px-3 py-2 font-mono text-xs text-[#FF6B6B]">{code}</td>
                <td className="px-3 py-2 text-gray-700 text-xs">{t(`brain.clients.teams.${code}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
        <Brain className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700">
          {t('brain.clients.step4.tip')}
        </p>
      </div>
    </div>
  );
}

// ─── Step 5 ────────────────────────────────────────────────────────────────────

function Step5({
  form, mode, onSubmit, isLoading, prUrl, error,
}: {
  form: FormState; mode: FormMode; onSubmit: () => void;
  isLoading: boolean; prUrl: string | null; error: string | null;
}) {
  const { t } = useTranslation();
  const hasBq = !!form.bqName.trim();
  const isEdit = mode.type === 'edit';

  if (prUrl) {
    return (
      <PrOpenedNotice
        prUrl={prUrl}
        title={isEdit ? t('brain.clients.form.success_edit_title') : t('brain.clients.form.success_new_title')}
        body={isEdit ? t('brain.clients.form.success_edit_body') : t('brain.clients.form.success_new_body', { name: form.displayName })}
      />
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        {isEdit ? t('brain.clients.step5.edit_hint') : t('brain.clients.step5.new_hint')}
      </p>
      <div className="rounded-lg border divide-y text-sm">
        <PreviewRow label={t('brain.clients.step5.preview_client')} value={form.displayName} />
        <PreviewRow label={t('brain.clients.step5.preview_id')}     value={<code className="font-mono text-xs">{form.clientId}</code>} />
        <PreviewRow label={t('brain.clients.step5.preview_slack')}  value={
          <ul className="space-y-0.5">
            {form.slackChannels.map((ch, i) => (
              <li key={i} className="font-mono text-xs">{ch.channelName} ({ch.channelId})</li>
            ))}
            {form.hasExternalWorkspace && (
              <li className="text-xs text-blue-600 font-medium mt-0.5">
                {t('brain.clients.workspace_externo')}{form.externalChannelNames.length > 0 ? `: ${form.externalChannelNames.map((n) => `#${n}`).join(', ')}` : ` ${t('brain.clients.pendiente_invitacion')}`}
              </li>
            )}
          </ul>
        } />
        <PreviewRow label={t('brain.clients.step5.preview_bq')} value={hasBq ? t('brain.clients.step5.preview_bq_yes') : t('brain.clients.step5.preview_bq_no')} />
        <PreviewRow label={t('brain.clients.step5.preview_files')} value={
          <ul className="space-y-0.5 text-xs font-mono text-gray-600">
            <li>brain-repo/clients/{form.clientId}/CLAUDE.md</li>
            {hasBq && <li>bigquery-brain.ts</li>}
            <li>slack-brain-sync.ts</li>
            {!isEdit && <li>google-meet-brain.ts</li>}
            {!isEdit && <li>ClientSelector.tsx</li>}
          </ul>
        } />
      </div>
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}
      <Button onClick={onSubmit} disabled={isLoading}
        className="w-full bg-[#FF6B6B] hover:bg-[#e85d5d] text-white">
        {isLoading
          ? (isEdit ? t('brain.clients.form.updating') : t('brain.clients.form.opening_pr'))
          : (isEdit ? t('brain.clients.form.save') : t('brain.clients.form.open_pr'))}
      </Button>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 px-3 py-2.5">
      <span className="text-gray-500 w-20 shrink-0 text-xs">{label}</span>
      <span className="text-gray-900 flex-1 text-sm">{value}</span>
    </div>
  );
}

// ─── Proposal Card ─────────────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  onEdit,
  onDelete,
}: {
  proposal: {
    clientId: string; displayName: string;
    slackChannels: Array<{ channelId: string; channelName: string }>;
    capacityNames: string[]; jotformName: string; allocatorNames: string[];
    prUrl: string; prNumber: number; branchName: string;
    createdAt: string; prStatus: 'open' | 'merged' | 'closed';
    kind?: string; baseClientId?: string;
  };
  onEdit:   () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const isAddChannels = proposal.kind === 'add-channels';
  const isProfile = proposal.kind === 'pml-profile';
  const canEdit = proposal.prStatus === 'open' && !isAddChannels && !isProfile;

  return (
    <div className="bg-white rounded-xl border shadow-xs p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900">{proposal.displayName}</p>
            {isAddChannels && (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/20">
                <Hash className="w-2.5 h-2.5" /> {t('brain.clients.canal_nuevo')}
              </span>
            )}
            {isProfile && (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/20">
                <Megaphone className="w-2.5 h-2.5" /> {t('brain.clients.perfil_pml_badge')}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{proposal.baseClientId ?? proposal.clientId}</p>
        </div>
        <StatusBadge status={proposal.prStatus} />
      </div>

      <div className="text-xs text-gray-600 space-y-1">
        <p>
          <span className="text-gray-400">Slack:</span>{' '}
          {t(proposal.slackChannels.length === 1 ? 'brain.clients.my_proposals.slack_one' : 'brain.clients.my_proposals.slack_other', { count: proposal.slackChannels.length })}
          {' '}—{' '}
          {proposal.slackChannels.map((ch) => ch.channelName).join(', ')}
        </p>
        {(proposal.capacityNames.length > 0 || proposal.jotformName) && (
          <p><span className="text-gray-400">BigQuery:</span> {t('brain.clients.my_proposals.bq_configured')}</p>
        )}
        <p className="text-gray-400">
          {new Date(proposal.createdAt).toLocaleDateString(i18n.language, {
            day: '2-digit', month: 'short', year: 'numeric',
          })}
        </p>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <a href={proposal.prUrl} target="_blank" rel="noreferrer"
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors">
          <ExternalLink className="w-3 h-3" /> {t('brain.clients.my_proposals.view_pr', { number: proposal.prNumber })}
        </a>
        <span className="text-gray-200">|</span>
        {canEdit && (
          <button onClick={onEdit}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors">
            <Pencil className="w-3 h-3" /> {t('brain.clients.my_proposals.edit')}
          </button>
        )}
        {canEdit && <span className="text-gray-200">|</span>}
        {!confirming ? (
          <button onClick={() => setConfirming(true)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors">
            <Trash2 className="w-3 h-3" /> {canEdit ? t('brain.clients.my_proposals.cancel_proposal') : t('brain.clients.my_proposals.delete')}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600 font-medium">{t('brain.clients.my_proposals.confirm_prompt')}</span>
            <button onClick={onDelete}
              className="text-xs text-red-600 hover:text-red-800 font-semibold">{t('brain.clients.my_proposals.confirm_yes')}</button>
            <button onClick={() => setConfirming(false)}
              className="text-xs text-gray-500 hover:text-gray-800">{t('brain.clients.my_proposals.confirm_no')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mis Solicitudes tab ───────────────────────────────────────────────────────

function MisSolicitudes({ onNewRequest, onEdit }: {
  onNewRequest: () => void;
  onEdit: (p: EditProposal) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, refetch, isRefetching } = trpc.brain.myProposals.useQuery();
  const deleteMutation = trpc.brain.deleteProposal.useMutation({
    onSuccess: () => { refetch(); },
  });

  const proposals = data?.proposals ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {proposals.length === 0 && !isLoading
            ? t('brain.clients.my_proposals.none')
            : t(proposals.length === 1 ? 'brain.clients.my_proposals.count_one' : 'brain.clients.my_proposals.count_other', { count: proposals.length })}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} disabled={isRefetching}
            className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
            title={t('brain.clients.my_proposals.refresh')}>
            <RefreshCw className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          </button>
          <Button size="sm" onClick={onNewRequest}
            className="bg-[#FF6B6B] hover:bg-[#e85d5d] text-white gap-1">
            <Plus className="w-3.5 h-3.5" /> {t('brain.clients.my_proposals.new_btn')}
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-sm text-gray-400">{t('brain.clients.my_proposals.loading')}</div>
      )}

      {!isLoading && proposals.length === 0 && (
        <div className="text-center py-12 border rounded-xl border-dashed">
          <Brain className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t('brain.clients.my_proposals.empty_state')}</p>
          <button onClick={onNewRequest}
            className="mt-3 text-sm text-[#FF6B6B] hover:underline font-medium">
            {t('brain.clients.my_proposals.empty_cta')}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {proposals.map((p) => (
          <ProposalCard
            key={p.clientId}
            proposal={p}
            onEdit={() => onEdit(p)}
            onDelete={() =>
              deleteMutation.mutate({ clientId: p.clientId, prNumber: p.prNumber })
            }
          />
        ))}
      </div>
    </div>
  );
}

// ─── Form (New + Edit) ─────────────────────────────────────────────────────────

function ClientForm({
  initialForm,
  mode,
  onDone,
}: {
  initialForm: FormState;
  mode:        FormMode;
  onDone:      () => void;
}) {
  const [step, setStep]   = useState(0);
  const [form, setForm]   = useState<FormState>(initialForm);
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();
  const proposeMutation = trpc.brain.proposeClient.useMutation();
  const updateMutation  = trpc.brain.updateProposal.useMutation();
  const isEdit = mode.type === 'edit';

  const canAdvance = (): boolean => {
    if (step === 0) return form.displayName.trim().length >= 2 && /^[a-z0-9-]{2,30}$/.test(form.clientId);
    if (step === 1) return form.slackChannels.length > 0 || form.hasExternalWorkspace;
    // Blindaje: sin BigQuery solo a propósito (checkbox), nunca por omisión.
    if (step === 2) return form.bqName.trim().length > 0 || form.bqAllocator.length > 0 || !!form.bqJotform || form.noBq;
    return true;
  };

  const handleSubmit = async () => {
    setError(null);
    // Varios nombres separados por coma (ej. "RAPPI, BET_RAPPI"): todos van a
    // Capacity y al asignador de horas; el primero es el de Jotform (NPS).
    // Cada fuente con sus propios nombres (precargados de BigQuery, editables).
    const bqList = form.bqName.split(',').map((x) => x.trim()).filter(Boolean);
    const capacityArr  = bqList.length ? bqList : undefined;
    const allocatorArr = form.bqAllocator.length ? form.bqAllocator : (bqList.length ? bqList : undefined);
    const jotformName  = form.bqJotform || bqList[0];
    const noBq         = form.noBq || undefined;

    try {
      if (isEdit && mode.type === 'edit') {
        await updateMutation.mutateAsync({
          clientId:             form.clientId,
          displayName:          form.displayName,
          slackChannels:        form.slackChannels,
          hasExternalWorkspace: form.hasExternalWorkspace || undefined,
          externalChannelNames: form.externalChannelNames.length > 0 ? form.externalChannelNames : undefined,
          capacityNames:        capacityArr,
          jotformName,
          allocatorNames:       allocatorArr,
          noBq,
          branchName:           mode.branchName,
          prNumber:             mode.prNumber,
          prUrl:                mode.prUrl,
        });
        setPrUrl(mode.prUrl);
      } else {
        const result = await proposeMutation.mutateAsync({
          clientId:             form.clientId,
          displayName:          form.displayName,
          slackChannels:        form.slackChannels,
          hasExternalWorkspace: form.hasExternalWorkspace || undefined,
          externalChannelNames: form.externalChannelNames.length > 0 ? form.externalChannelNames : undefined,
          capacityNames:        capacityArr,
          jotformName,
          allocatorNames:       allocatorArr,
          noBq,
        });
        setPrUrl(result.prUrl);
      }
    } catch (err) {
      setError((err as Error).message ?? 'Error. Intentá de nuevo.');
    }
  };

  const isLoading = proposeMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-0">
      {isEdit && (
        <div className="flex items-center gap-2 mb-6">
          <button onClick={onDone} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> {t('brain.clients.form.back')}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-900">{t('brain.clients.form.edit_breadcrumb', { name: form.displayName })}</span>
        </div>
      )}

      <StepBar current={step} labels={CLIENT_STEP_KEYS.map((k) => t(k))} />

      {step === 0 && <Step1 form={form} setForm={setForm} isEdit={isEdit} />}
      {step === 1 && <Step2 form={form} setForm={setForm} />}
      {step === 2 && <Step3 form={form} setForm={setForm} />}
      {step === 3 && <Step4 form={form} />}
      {step === 4 && (
        <Step5
          form={form} mode={mode} onSubmit={handleSubmit}
          isLoading={isLoading} prUrl={prUrl} error={error}
        />
      )}

      {!prUrl && (
        <div className="flex justify-between mt-8">
          <Button variant="ghost" onClick={() => {
            if (step === 0 && isEdit) { onDone(); return; }
            setStep((s) => Math.max(0, s - 1));
          }} className="gap-1">
            <ChevronLeft className="w-4 h-4" /> {step === 0 && isEdit ? t('brain.clients.form.cancel') : t('brain.clients.form.previous')}
          </Button>
          {step < 4 && (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canAdvance()}
              className="bg-[#FF6B6B] hover:bg-[#e85d5d] text-white gap-1">
              {t('brain.clients.form.next')} <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {prUrl && (
        <div className="mt-6 text-center">
          <button onClick={onDone} className="text-sm text-gray-500 hover:text-gray-800 underline">
            {t('brain.clients.form.back_to_proposals')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Add channels to existing client ────────────────────────────────────────────

function AddChannelsForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [clientId, setClientId] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm());
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const addMutation = trpc.brain.addClientChannels.useMutation();

  const canSubmit = !!clientId && (form.slackChannels.length > 0 || form.hasExternalWorkspace);

  const handleSubmit = async () => {
    setError(null);
    try {
      const res = await addMutation.mutateAsync({
        clientId,
        slackChannels: form.slackChannels,
        hasExternalWorkspace: form.hasExternalWorkspace || undefined,
        externalChannelNames: form.hasExternalWorkspace ? form.externalChannelNames : undefined,
      });
      setPrUrl(res.prUrl);
    } catch (e) {
      setError((e as Error).message || 'No se pudo crear la solicitud');
    }
  };

  if (prUrl) {
    return (
      <PrOpenedNotice prUrl={prUrl} title={t('brain.clients.solicitud_creada')} body={t('brain.clients.se_abrio_un_pr_para_sumar')}>
        <div><Button variant="outline" onClick={onDone}>{t('brain.clients.volver_a_mis_solicitudes')}</Button></div>
      </PrOpenedNotice>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-xs font-semibold text-gray-700">{t('brain.clients.cliente_existente')}</Label>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="mt-1.5 w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-[#FF6B6B]"
        >
          <option value="">{t('brain.clients.elegi_un_cliente')}</option>
          {Object.entries(BRAIN_CLIENTS).map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
      </div>

      <Step2 form={form} setForm={setForm} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-3 border-t">
        <Button variant="outline" onClick={onDone}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || addMutation.isPending}
          className="bg-[#FF6B6B] hover:bg-[#e85d5d] text-white"
        >
          {addMutation.isPending ? 'Creando…' : t('brain.clients.crear_solicitud')}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

type EditProposal = {
  clientId: string; displayName: string;
  slackChannels: Array<{ channelId: string; channelName: string }>;
  capacityNames: string[]; jotformName: string; allocatorNames: string[];
  prUrl: string; prNumber: number; branchName: string;
  createdAt: string; prStatus: 'open' | 'merged' | 'closed';
};

type PageView =
  | { type: 'list' }
  | { type: 'choose' }
  | { type: 'add-channels' }
  | { type: 'new' }
  | { type: 'new-profile' }
  | { type: 'edit'; proposal: EditProposal };

/** Destino de una solicitud nueva: General (lo de siempre) o un área (Paid Media primera; las demás a medida que se sumen). */
type Destination = 'general' | 'paid-media';

type Tab = 'solicitudes' | 'salud' | 'calidad' | 'pipeline';

export function BrainClientsPage() {
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  // `?new=pml` abre directo el alta de perfil (link que deja /add-client del MCP con un borrador).
  const [view, setView] = useState<PageView>(searchParams.get('new') === 'pml' ? { type: 'new-profile' } : { type: 'list' });
  const [destination, setDestination] = useState<Destination>('general');
  const [tab, setTab]   = useState<Tab>(
    searchParams.get('tab') === 'pipeline' ? 'pipeline'
      : searchParams.get('tab') === 'calidad' && isAdmin ? 'calidad' : searchParams.get('tab') === 'salud' && isAdmin ? 'salud' : 'solicitudes'
  );
  // Panel de permisos del área Pipeline comercial: governance admins (no necesariamente admins de plataforma).
  const { data: pipelineAccess, isLoading: pipelineAccessLoading } = trpc.brain.pipelineGovernance.checkAccess.useQuery(undefined, { staleTime: 60_000, retry: false });
  const canAdminPipeline = isAdmin || !!pipelineAccess?.canAdmin;
  // `?tab=pipeline` para quien no puede: vuelve a Solicitudes en vez de dejar la página vacía (frontend-auditor 01/10).
  const activeTab: Tab = tab === 'pipeline' && !canAdminPipeline && !pipelineAccessLoading ? 'solicitudes' : tab;

  const startEdit = (p: EditProposal) => {
    setView({ type: 'edit', proposal: p });
  };

  const makeEditForm = (p: {
    displayName: string; clientId: string;
    slackChannels: Array<{ channelId: string; channelName: string }>;
    capacityNames: string[]; jotformName: string; allocatorNames: string[];
    prUrl: string; prNumber: number; branchName: string;
    createdAt: string; prStatus: 'open' | 'merged' | 'closed';
  }): FormState => ({
    displayName:          p.displayName,
    clientId:             p.clientId,
    clientIdTouched:      true,
    slackChannels:        p.slackChannels,
    hasExternalWorkspace: false,
    externalChannelNames: [],
    bqName:               p.capacityNames.join(', '),
    bqAllocator:          p.allocatorNames ?? [],
    bqJotform:            p.jotformName ?? '',
    bqAuto:               true,
    noBq:                 false,
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/brain')}
          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
          aria-label={t('brain.clients.back_to_brain')}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#FF6B6B]" />
          <span className="font-semibold text-gray-900">{t('brain.clients.page_title')}</span>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-xl bg-white rounded-xl border shadow-xs p-6">
          {/* Tab bar — only show if no sub-view is open */}
          {view.type === 'list' && (
            <div className="flex items-center gap-1 mb-6 border-b pb-3">
              <button
                onClick={() => setTab('solicitudes')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'solicitudes'
                    ? 'bg-[#FF6B6B]/10 text-[#FF6B6B]'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Brain className="w-3.5 h-3.5" /> {t('brain.clients.mis_solicitudes')}
              </button>
              {isAdmin && (
                <button
                  onClick={() => setTab('salud')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'salud'
                      ? 'bg-[#FF6B6B]/10 text-[#FF6B6B]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Activity className="w-3.5 h-3.5" /> Salud
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setTab('calidad')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'calidad'
                      ? 'bg-[#FF6B6B]/10 text-[#FF6B6B]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Gauge className="w-3.5 h-3.5" /> Calidad
                </button>
              )}
              {canAdminPipeline && (
                <button
                  onClick={() => setTab('pipeline')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'pipeline'
                      ? 'bg-[#FF6B6B]/10 text-[#FF6B6B]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> {t('brain.clients.tab_pipeline')}
                </button>
              )}
            </div>
          )}

          {view.type === 'list' && activeTab === 'solicitudes' && (
            <MisSolicitudes
              onNewRequest={() => setView({ type: 'choose' })}
              onEdit={startEdit}
            />
          )}

          {view.type === 'list' && activeTab === 'salud' && isAdmin && (
            <BrainHealthTab />
          )}

          {view.type === 'list' && activeTab === 'calidad' && isAdmin && (
            <BrainQualityTab />
          )}

          {view.type === 'list' && activeTab === 'pipeline' && canAdminPipeline && (
            <BrainPipelineGovernanceTab />
          )}

          {view.type === 'choose' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button onClick={() => setView({ type: 'list' })}
                  className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Volver
                </button>
                <span className="text-gray-300">/</span>
                <span className="text-sm font-medium text-gray-900">{t('brain.clients.nueva_solicitud')}</span>
              </div>
              <p className="text-sm text-gray-500 mb-3">{t('brain.clients.que_queres_hacer')}</p>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-gray-700">{t('brain.clients.destino_label')}</Label>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value as Destination)}
                  className="mt-1.5 w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-[#FF6B6B]"
                >
                  <option value="general">{t('brain.clients.destino_general')}</option>
                  <option value="paid-media">{t('brain.clients.destino_paid_media')}</option>
                </select>
              </div>
              {destination === 'paid-media' && (
                <div className="grid gap-3">
                  <button
                    onClick={() => setView({ type: 'new-profile' })}
                    className="flex items-start gap-3 p-4 rounded-xl border text-left hover:border-[#FF6B6B] hover:bg-[#FF6B6B]/5 transition-colors"
                  >
                    <span className="mt-0.5 p-2 rounded-lg bg-[#FF6B6B]/10 text-[#FF6B6B]"><Megaphone className="w-4 h-4" /></span>
                    <div>
                      <p className="font-semibold text-gray-900">{t('brain.clients.perfil_pml_card_title')}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t('brain.clients.perfil_pml_card_desc')}</p>
                    </div>
                  </button>
                </div>
              )}
              {destination === 'general' && (
              <div className="grid gap-3">
                <button
                  onClick={() => setView({ type: 'new' })}
                  className="flex items-start gap-3 p-4 rounded-xl border text-left hover:border-[#FF6B6B] hover:bg-[#FF6B6B]/5 transition-colors"
                >
                  <span className="mt-0.5 p-2 rounded-lg bg-[#FF6B6B]/10 text-[#FF6B6B]"><UserPlus className="w-4 h-4" /></span>
                  <div>
                    <p className="font-semibold text-gray-900">{t('brain.clients.cliente_nuevo')}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('brain.clients.onboardear_un_cliente_que_todavia_no')}</p>
                  </div>
                </button>
                <button
                  onClick={() => setView({ type: 'add-channels' })}
                  className="flex items-start gap-3 p-4 rounded-xl border text-left hover:border-brand-green hover:bg-brand-green/5 transition-colors"
                >
                  <span className="mt-0.5 p-2 rounded-lg bg-brand-green/10 text-brand-green"><Hash className="w-4 h-4" /></span>
                  <div>
                    <p className="font-semibold text-gray-900">{t('brain.clients.agregar_canal_a_un_cliente_existente')}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('brain.clients.sumar_uno_o_mas_canales_de')}</p>
                  </div>
                </button>
              </div>
              )}
            </>
          )}

          {view.type === 'new-profile' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button onClick={() => setView({ type: 'choose' })}
                  className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> {t('brain.clients.form.back')}
                </button>
                <span className="text-gray-300">/</span>
                <span className="text-sm font-medium text-gray-900">{t('brain.clients.perfil_pml_breadcrumb')}</span>
              </div>
              <BrainProfileForm onDone={() => setView({ type: 'list' })} />
            </>
          )}

          {view.type === 'add-channels' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button onClick={() => setView({ type: 'choose' })}
                  className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Volver
                </button>
                <span className="text-gray-300">/</span>
                <span className="text-sm font-medium text-gray-900">{t('brain.clients.agregar_canal_a_cliente_existente')}</span>
              </div>
              <AddChannelsForm onDone={() => setView({ type: 'list' })} />
            </>
          )}

          {view.type === 'new' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button onClick={() => setView({ type: 'list' })}
                  className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> {t('brain.clients.form.back')}
                </button>
                <span className="text-gray-300">/</span>
                <span className="text-sm font-medium text-gray-900">{t('brain.clients.form.new_breadcrumb')}</span>
              </div>
              <ClientForm
                initialForm={emptyForm()}
                mode={{ type: 'new' }}
                onDone={() => setView({ type: 'list' })}
              />
            </>
          )}

          {view.type === 'edit' && (
            <ClientForm
              initialForm={makeEditForm(view.proposal)}
              mode={{
                type: 'edit',
                branchName:      view.proposal.branchName,
                prNumber:        view.proposal.prNumber,
                prUrl:           view.proposal.prUrl,
                originalClientId: view.proposal.clientId,
              }}
              onDone={() => setView({ type: 'list' })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
